import React, { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  getPlans,
  createSubscriptionCheckout,
  getBillingConfig,
  createPlanRazorpayOrder,
  verifyPlanRazorpayPayment,
  BillingConfig,
} from "../../services/api";
import { activeCurrencySymbol } from "../../utils/currency";
import { loadRazorpayCheckout, RazorpayResponse } from "../../utils/razorpay";
import SubscriptionSummary, {
  describeSubscription,
  useSubscriptionInfo,
} from "../../components/SubscriptionSummary";

interface Plan {
  _id: string;
  name: string;
  durationMonths: number;
  price: number; // minor units (cents / paise)
  maxLocations?: number;
}

// Plans are charged in the platform currency set by master admin.
const formatPrice = (minor: number) => `${activeCurrencySymbol()}${(minor / 100).toFixed(2)}`;

const ChoosePlan: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoadingId, setCheckoutLoadingId] = useState<string | null>(null);
  const { info: subscription } = useSubscriptionInfo();
  const summary = subscription ? describeSubscription(subscription) : null;
  // How the master admin collects plan payments (Stripe or Razorpay).
  const [billing, setBilling] = useState<BillingConfig | null>(null);
  useEffect(() => {
    getBillingConfig()
      .then((res) => setBilling(res.data.data))
      .catch(() => setBilling(null));
  }, []);
  const isRazorpay = billing?.gateway === "razorpay";
  // A Stripe subscription auto-renews, so the active plan can't be bought
  // again; Razorpay payments are one-time, so buying again extends it.
  const activePlanId =
    !isRazorpay && summary && summary.status === "active" && !summary.isOver
      ? subscription?.currentPlan?._id
      : undefined;
  const currentPlanId =
    summary && summary.status === "active" && !summary.isOver
      ? subscription?.currentPlan?._id
      : undefined;

  let subtitle = "Pick a plan to keep using the POS.";
  if (summary?.isOver) {
    subtitle = summary.isTrial
      ? "Your free trial has ended. Pick a plan to keep using the POS."
      : "Your subscription is no longer active. Pick a plan to keep using the POS.";
  } else if (summary?.isTrial) {
    subtitle = "You're on the free trial. Pick a plan any time to continue after it ends.";
  } else if (summary) {
    subtitle = "Your details are below. You can switch to a different plan any time.";
  }

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const res = await getPlans();
        setPlans(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load plans. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const payWithRazorpay = async (planId: string) => {
    if (!(await loadRazorpayCheckout())) {
      toast.error("Could not load Razorpay. Check your internet connection.");
      return;
    }
    const { data } = await createPlanRazorpayOrder(planId);
    const { orderId, amount, currency, keyId, planName } = data.data;
    await new Promise<void>((resolve) => {
      const checkout = new window.Razorpay({
        key: keyId,
        order_id: orderId,
        amount,
        currency,
        name: planName,
        handler: async (response: RazorpayResponse) => {
          try {
            await verifyPlanRazorpayPayment(response);
            toast.success("Payment successful! Your plan is active.");
            // Reload so the admin panel picks up the unlocked account.
            window.location.href = "/admin";
          } catch (err: any) {
            toast.error(err.response?.data?.error || "Payment verification failed");
          } finally {
            resolve();
          }
        },
        modal: { ondismiss: () => resolve() },
      });
      checkout.on("payment.failed", (resp: any) => {
        toast.error(resp?.error?.description || "Payment failed");
      });
      checkout.open();
    });
  };

  const handleChoose = async (planId: string) => {
    if (isRazorpay) {
      try {
        setCheckoutLoadingId(planId);
        await payWithRazorpay(planId);
      } catch (error: any) {
        toast.error(error.response?.data?.error || "Could not start payment.");
      } finally {
        setCheckoutLoadingId(null);
      }
      return;
    }
    try {
      setCheckoutLoadingId(planId);
      const res = await createSubscriptionCheckout(planId);
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Could not start checkout.");
    } finally {
      setCheckoutLoadingId(null);
    }
  };

  return (
    <div className="mx-auto min-h-screen max-w-4xl p-4 pt-12 lg:p-8">
      <div className="mb-10 text-center">
        <h1 className="text-3xl font-bold text-gray-800">Choose a Plan</h1>
        <p className="mt-2 text-gray-500">{subtitle}</p>
      </div>

      {billing && !billing.configured && (
        <div className="mb-6 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-800">
          Online plan payments aren't set up yet. Please contact the platform admin.
        </div>
      )}

      {subscription && (
        <div className="mb-8">
          <SubscriptionSummary info={subscription} />
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : plans.length === 0 ? (
        <p className="text-center text-gray-500">
          No plans are available right now. Please contact support.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          {plans.map((plan) => {
            const isCurrent = plan._id === currentPlanId;
            const notSetUp = billing !== null && !billing.configured;
            return (
              <div
                key={plan._id}
                className={`flex flex-col rounded-xl border bg-white p-6 shadow-sm ${
                  isCurrent ? "border-2 border-blue-600" : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-lg font-semibold text-gray-800">{plan.name}</h2>
                  {isCurrent && (
                    <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                      Current
                    </span>
                  )}
                </div>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {formatPrice(plan.price)}
                </p>
                <p className="mb-6 text-sm text-gray-500">
                  {isRazorpay ? "for" : "every"} {plan.durationMonths} month
                  {plan.durationMonths > 1 ? "s" : ""}
                </p>
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-800">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Up to {plan.maxLocations || 1} location{(plan.maxLocations || 1) > 1 ? "s" : ""}
                </div>
                <div className="mb-6 flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  {isRazorpay
                    ? "One-time payment — buy again to extend"
                    : "Auto-renews until canceled"}
                </div>
                <button
                  onClick={() => handleChoose(plan._id)}
                  disabled={
                    notSetUp || checkoutLoadingId === plan._id || plan._id === activePlanId
                  }
                  className="mt-auto rounded-md bg-blue-600 px-4 py-2 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {plan._id === activePlanId
                    ? "Current Plan"
                    : checkoutLoadingId === plan._id
                      ? isRazorpay
                        ? "Opening..."
                        : "Redirecting..."
                      : isRazorpay && isCurrent
                        ? "Extend Plan"
                        : "Choose Plan"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChoosePlan;
